
import { Options, Vue } from "vue-class-component";
import MIcon from "@/components/MIcon.vue";
import MBaseModal from "@/components/MBaseModal.vue";
import MButton from "@/components/form/MButton.vue";
import MTextField from "@/components/form/MTextField.vue";
import MTextArea from "@/components/form/MTextArea.vue";
import { Student } from "@/entities/student";
import store from "@/store";
import "firebase/firestore";
import "firebase/functions";
import {
  Unit,
  reflectionCollectionKey,
  convertToReflection
} from "@/entities/reflection";
import MSLineChart from "@/components/analyze/MSLineChart.vue";
import dayjs from "dayjs";
import "dayjs/locale/ja";
import firebase from "firebase/app";
import { isProd } from "@/env";
import {
  fillMissingTimeRange,
  generateReflectionUid,
  getUnixFromUid,
  isFutureDate,
  ReflectionWithTimeRange,
  sortReflectionsByUid,
  validateTimeRange
} from "@/utils/reflection";
import { getHeartRateDataWithTimeRange } from "@/api/polar";
import { DeviceHeartRateData } from "@/entities/polar";
dayjs.locale("ja");

const MAX_LENGTH = 100000;

@Options({
  components: {
    MIcon,
    MBaseModal,
    MButton,
    MTextField,
    MSLineChart,
    MTextArea
  },
  emits: ["close", "aiReflectionUpdate"],
  props: {
    student: Object,
    unit: Object,
    canEdit: Boolean
  }
})
export default class MsEditModal extends Vue {
  student!: Student;
  unit: Unit | null = null;
  reflections: ReflectionWithTimeRange[] = [];
  datasets: {
    label: string;
    data: number[];
    borderColor: string;
    borderWidth: number;
    tension: number;
  }[] = [];
  labels: string[] = [];
  min = 0;
  max = 100;
  canEdit = false;
  unitName = "";
  graphTitle = "";
  secondRowTitle = "";
  thirdRowTitle = "";
  status:
    | "display"
    | "recordInput"
    | "recordEdit"
    | "unitReflectionInput"
    | "unitReflectionEdit"
    | "heartRate" = "display";
  promptTrimmed = false;

  generateObjectPath(propName: string) {
    return "learningCard.modal" + "." + propName;
  }

  get hasPolarDeviceid(): boolean {
    return !!this.student.data.polarDeviceId;
  }

  get translatedValue() {
    return store.state.translatedValue;
  }

  getMaxLength() {
    return MAX_LENGTH;
  }

  close() {
    this.$emit("close");
  }

  formatDate(uid: string): string {
    const unix = getUnixFromUid(uid);
    return dayjs.unix(unix).format("MM/DD(ddd)");
  }

  async getReflections() {
    if (!this.unit) return [];
    const snapshot = await this.unit.ref
      .collection(reflectionCollectionKey)
      .get();
    if (snapshot.empty) return [];
    return snapshot.docs
      .filter(doc => doc.exists && doc.data())
      .map(doc => convertToReflection(doc.data(), doc.ref));
  }

  // 記録登録系統
  recordInput: {
    timeRangeStart: string;
    timeRangeEnd: string;
    record: number;
    reflection: string;
    description: string;
  } = {
    timeRangeStart: dayjs()
      .startOf("day")
      .format("YYYY-MM-DDTHH:mm"),
    timeRangeEnd: dayjs()
      .endOf("day")
      .format("YYYY-MM-DDTHH:mm"),
    record: 0,
    reflection: "",
    description: ""
  };
  recordEntered = true;
  recordReflectionEntered = false;
  recordDescriptionEntered = false;
  validRecordInput = true;
  validRecordReflectionInput = false;
  validRecordDescriptionInput = false;

  get validTimeRangeInput() {
    const startUnix = dayjs(this.recordInput.timeRangeStart).unix();
    const endUnix = dayjs(this.recordInput.timeRangeEnd).unix();
    return validateTimeRange(startUnix, endUnix);
  }

  get validReflectionInput() {
    return (
      this.validRecordInput &&
      this.validRecordReflectionInput &&
      this.validRecordDescriptionInput &&
      this.validTimeRangeInput
    );
  }
  openRecordInput() {
    this.status = "recordInput";
  }
  clearRecordInput() {
    this.recordInput = {
      timeRangeStart: dayjs()
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm"),
      timeRangeEnd: dayjs()
        .endOf("day")
        .format("YYYY-MM-DDTHH:mm"),
      record: 0,
      reflection: "",
      description: ""
    };
    this.recordEntered = true;
    this.recordReflectionEntered = false;
    this.recordDescriptionEntered = false;
    this.validRecordInput = true;
    this.validRecordReflectionInput = false;
    this.validRecordDescriptionInput = false;
  }
  async registerRecord() {
    if (!this.unit) return;
    if (!this.validReflectionInput) return;
    if (this.recordInput.reflection.length === 0) {
      alert("振り返りが入力されていません。");
      return;
    }
    if (this.recordInput.description.length === 0) {
      alert("自由入力が入力されていません。");
      return;
    }

    const startUnix = dayjs(this.recordInput.timeRangeStart).unix();
    const endUnix = dayjs(this.recordInput.timeRangeEnd).unix();

    if (!validateTimeRange(startUnix, endUnix)) {
      alert("開始日時は終了日時より前である必要があります。");
      return;
    }

    if (isFutureDate(startUnix) || isFutureDate(endUnix)) {
      alert("未来の日付は登録できません。");
      return;
    }

    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "登録中...");

      const docId = generateReflectionUid(
        this.reflections,
        dayjs(this.recordInput.timeRangeStart).format("YYYY-MM-DD")
      );
      const newRef = this.unit.ref
        .collection(reflectionCollectionKey)
        .doc(docId);
      await newRef.set({
        uid: newRef.id,
        record: this.recordInput.record,
        reflection: this.recordInput.reflection,
        description: this.recordInput.description,
        timeRange: {
          start: startUnix,
          end: endUnix
        },
        createdAt: startUnix,
        updatedAt: dayjs().unix()
      });
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      this.$router.go(0);
    } catch (e) {
      alert("記録の登録に失敗しました。");
      console.error(e);
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    }
  }

  //記録編集系統
  reflectionToEdit: ReflectionWithTimeRange | null = null;
  eRecordInput: {
    timeRangeStart: string;
    timeRangeEnd: string;
    record: number;
    reflection: string;
    description: string;
    createdAt: number;
  } = {
    timeRangeStart: dayjs()
      .startOf("day")
      .format("YYYY-MM-DDTHH:mm"),
    timeRangeEnd: dayjs()
      .endOf("day")
      .format("YYYY-MM-DDTHH:mm"),
    record: 0,
    reflection: "",
    description: "",
    createdAt: 0
  };
  eRecordEntered = true;
  eRecordReflectionEntered = true;
  eRecordDescriptionEntered = true;
  eValidRecordInput = true;
  eValidRecordReflectionInput = true;
  eValidRecordDescriptionInput = true;

  get eValidTimeRangeInput() {
    const startUnix = dayjs(this.eRecordInput.timeRangeStart).unix();
    const endUnix = dayjs(this.eRecordInput.timeRangeEnd).unix();
    return validateTimeRange(startUnix, endUnix);
  }

  get eValidReflectionInput() {
    return (
      this.eValidRecordInput &&
      this.eValidRecordReflectionInput &&
      this.eValidRecordDescriptionInput &&
      this.eValidTimeRangeInput
    );
  }
  openRecordEditInput(reflectionId: string) {
    if (!reflectionId) return;
    if (!this.reflections.length) return;
    const targetReflection = this.reflections.find(
      r => r.data.uid === reflectionId
    );
    if (!targetReflection) {
      alert(`${reflectionId}の記録が見つかりません。`);
      return;
    }
    this.reflectionToEdit = { ...targetReflection } as ReflectionWithTimeRange;
    const { data } = targetReflection;
    this.eRecordInput = {
      timeRangeStart: dayjs
        .unix(data.timeRange.start)
        .format("YYYY-MM-DDTHH:mm"),
      timeRangeEnd: dayjs.unix(data.timeRange.end).format("YYYY-MM-DDTHH:mm"),
      record: data.record,
      reflection: data.reflection,
      description: data.description,
      createdAt: data.createdAt
    };
    this.status = "recordEdit";
  }
  clearRecordEditInput() {
    this.reflectionToEdit = null;
    this.eRecordInput = {
      timeRangeStart: dayjs()
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm"),
      timeRangeEnd: dayjs()
        .endOf("day")
        .format("YYYY-MM-DDTHH:mm"),
      record: 0,
      reflection: "",
      description: "",
      createdAt: 0
    };
    this.eRecordEntered = true;
    this.eRecordReflectionEntered = true;
    this.eRecordDescriptionEntered = true;
    this.eValidRecordInput = true;
    this.eValidRecordReflectionInput = true;
    this.eValidRecordDescriptionInput = true;
  }
  async updateRecord() {
    if (!this.unit) return;
    if (!this.reflectionToEdit) return;
    if (!this.eValidReflectionInput) return;

    if (this.eRecordInput.reflection.length === 0) {
      alert("振り返りが入力されていません。");
      return;
    }
    if (this.eRecordInput.description.length === 0) {
      alert("自由入力が入力されていません。");
      return;
    }

    const startUnix = dayjs(this.eRecordInput.timeRangeStart).unix();
    const endUnix = dayjs(this.eRecordInput.timeRangeEnd).unix();

    if (!validateTimeRange(startUnix, endUnix)) {
      alert("開始日時は終了日時より前である必要があります。");
      return;
    }

    if (isFutureDate(startUnix) || isFutureDate(endUnix)) {
      alert("未来の日付は登録できません。");
      return;
    }

    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "更新中...");

      const startDate = dayjs(this.eRecordInput.timeRangeStart).format(
        "YYYY-MM-DD"
      );
      const originalDate = dayjs
        .unix(this.reflectionToEdit.data.timeRange.start)
        .format("YYYY-MM-DD");

      if (originalDate !== startDate) {
        const newDocId = generateReflectionUid(this.reflections, startDate);
        // 日付に変更がある場合は、新しく作って、今のを削除する
        const newRef = this.unit.ref
          .collection(reflectionCollectionKey)
          .doc(newDocId);
        await Promise.all([
          newRef.set({
            uid: newRef.id,
            record: this.eRecordInput.record,
            reflection: this.eRecordInput.reflection,
            description: this.eRecordInput.description,
            timeRange: {
              start: startUnix,
              end: endUnix
            },
            createdAt: this.eRecordInput.createdAt,
            updatedAt: dayjs().unix()
          }),
          this.reflectionToEdit.ref.delete()
        ]);
      } else {
        await this.reflectionToEdit.ref.update({
          record: this.eRecordInput.record,
          reflection: this.eRecordInput.reflection,
          description: this.eRecordInput.description,
          timeRange: {
            start: startUnix,
            end: endUnix
          },
          updatedAt: dayjs().unix()
        });
      }

      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      this.$router.go(0);
    } catch (e) {
      alert("記録の更新に失敗しました。");
      console.error(e);
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    }
  }

  async deleteRecord() {
    if (!this.unit) return;
    if (!this.reflectionToEdit) return;
    if (!window.confirm("この振り返りを削除しても良いですか？")) return;
    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "削除中...");
      await this.reflectionToEdit.ref.delete();
    } catch (e) {
      alert("削除に失敗しました。");
      console.error(e);
    } finally {
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      this.$router.go(0);
    }
  }

  // 振り返り登録系統
  handleUnitReflectionInputs() {
    if (!this.unit) return;
    // 将来的に処理の内容を切り替える必要があるかもしれないので分岐を分けておく
    if (this.unit.data.selfReflection) {
      this.openUnitReflectionInput();
    } else {
      this.openUnitReflectionInput();
    }
  }
  unitReflection = "";
  validUnitReflection = false;
  openUnitReflectionInput() {
    this.unitReflection =
      this.unit && this.unit.data.selfReflection
        ? this.unit.data.selfReflection
        : "";
    this.validUnitReflection = this.unitReflection.length > 0 ? true : false;
    this.status = "unitReflectionInput";
  }
  clearUnitReflectionInput() {
    this.unitReflection = "";
    this.validUnitReflection = false;
    this.status = "display";
  }
  async registerUnitReflection() {
    if (!this.unit) return;
    if (!this.validUnitReflection) return;
    if (!this.unit.data.selfReflection && this.unitReflection.length === 0) {
      alert("振り返りが入力されていません。");
      return;
    }
    const text = this.unit.data.selfReflection ? "更新" : "登録";
    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", `振り返り${text}中...`);
      await this.unit.ref.update({ selfReflection: this.unitReflection });
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      this.$router.go(0);
    } catch (e) {
      alert(`振り返りの${text}に失敗しました。`);
      console.error(e);
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    }
  }

  //心拍数データ
  targetHeartRateData: {
    time: number;
    hr: number;
  }[] = [];
  get targetHeartRatesLabels() {
    if (!this.targetHeartRateData) return [];
    return this.targetHeartRateData.map(hr =>
      dayjs.unix(hr.time).format("HH:mm:ss")
    );
  }

  get targetHeartRatesDatasets() {
    if (!this.targetHeartRateData) return [];

    return [
      {
        label: "心拍数",
        data: this.targetHeartRateData.map(hr => hr.hr),
        borderColor: "rgb(75, 192, 192)",
        borderWidth: 2,
        tension: 0.1
      }
    ];
  }

  get heartRateTitle(): string {
    return this.targetHeartRateData.length > 0
      ? `心拍数の推移 ${dayjs
          .unix(this.targetHeartRateData[0].time)
          .format("YYYY/MM/DD HH:mm:ss")} ～ ${dayjs
          .unix(
            this.targetHeartRateData[this.targetHeartRateData.length - 1].time
          )
          .format("YYYY/MM/DD HH:mm:ss")}`
      : "";
  }

  get targetHeartRatesMin() {
    if (!this.targetHeartRateData) return 0;

    const buffer = 5;
    return Math.min(...this.targetHeartRateData.map(_ => _.hr)) - buffer;
  }

  get targetHeartRatesMax() {
    if (!this.targetHeartRateData) return 0;

    const buffer = 5;
    return Math.max(...this.targetHeartRateData.map(_ => _.hr)) + buffer;
  }

  async openHeartRateData(reflectionId: string) {
    if (!reflectionId) return;
    if (!this.reflections.length) return;
    const targetReflection = this.reflections.find(
      r => r.data.uid === reflectionId
    );
    if (!targetReflection) {
      alert(`${reflectionId}の記録が見つかりません。`);
      return;
    }

    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "データ取得中...");

      const { timeRange } = targetReflection.data;
      const res = await getHeartRateDataWithTimeRange({
        from: timeRange.start,
        to: timeRange.end
      });

      if (!res.history) {
        this.targetHeartRateData = [];
        this.status = "heartRate";
        return;
      }

      this.targetHeartRateData = Object.entries(res.history).map(
        ([time, hr]) => ({
          time: dayjs(time).unix(),
          hr
        })
      );

      this.status = "heartRate";
    } catch (e) {
      alert(
        "データの取得に失敗しました。" +
          "\n" +
          (e instanceof Error ? e.message : "unknown error")
      );
    } finally {
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    }
  }

  clearHeartRateData() {
    this.targetHeartRateData = [];
    this.status = "display";
  }

  //AI振り返り系統
  async getHeartRatesFromReflections(
    reflections: ReflectionWithTimeRange[]
  ): Promise<DeviceHeartRateData[]> {
    if (!reflections.length) return [];

    return (
      await Promise.all(
        reflections.map(({ data }) =>
          getHeartRateDataWithTimeRange({
            from: data.timeRange.start,
            to: data.timeRange.end
          })
        )
      )
    ).filter(
      data => data.history !== null && Object.keys(data.history).length > 0
    );
  }

  parseHeartRateData(
    heartRateData: DeviceHeartRateData[],
    options?: { taleEvery?: number }
  ): string {
    return heartRateData
      .map(({ history }) =>
        history
          ? Object.entries(history).map(([time, hr]) => ({
              time: dayjs(time).unix(),
              hr
            }))
          : null
      )
      .filter(_ => _ !== null)
      .map(
        // dataはnon-nullで確定だがeslintが通してくれないのでやむを得ずアサーション
        data => `
${dayjs.unix(data![0].time).format("YYYY/MM/DD")}
${data!
  .filter((_, i) =>
    options?.taleEvery !== undefined ? i % options.taleEvery === 0 : true
  )
  .map(({ time, hr }) => `${dayjs.unix(time).format("HH:mm:ss")} : ${hr}`)
  .join("\n")}
`
      )
      .join("=============");
  }

  async getGptMessage(): Promise<{ message: string; promptTrimmed: boolean }> {
    if (!this.unit) throw new Error("単元がありません。");
    if (this.reflections.length === 0)
      throw new Error("振り返りデータがありません。");
    let trimmed = false;
    let prompt = `
# 役割
あなたは授業をサポートするチューターです。生徒の学習記録に対してコメントし、生徒の振り返りをサポートする役割です。
# 指示
以下の手順で、生徒の授業での学習記録を元にポジティブなコメントを書いてください。
1. 生徒の最新の取り組みと数値データ（例：50m走の結果や提出物の数）を確認します。
2. 観点別評価の項目ごとに、生徒がどのように成長しているかを具体的に示します。
3. 観点別評価の項目ごとに、生徒の努力や改善が見られるポイントを強調して称賛します。
4. 観点別評価の項目ごとに、前回の記録と比較して進歩が見られる場合、その進歩を具体的に示し、さらに前向きなコメントを付け加えます。
5. 生徒がこれからも努力を続けられるように励ましのメッセージを付け加えます。
# 観点別評価
1. 知識・理解
   - 運動の合理的な実践に関する具体的な知識や、生涯にわたって運動に親しむための科学的知識、及び運動の特性に応じた基本的な技能を身に付けている。
   - 個人生活における健康・安全について、課題解決に役立つ知識や技能を身に付けている。
2. 思考・判断・表現
   - 自己の課題に応じた運動の取り組み方や、健康の保持及び体力を高めるための運動の組み合わせ方を工夫し、それらを表現している。
   - 個人生活における健康課題を把握し、その解決を目指して科学的に考え、判断し、それらを表現している。
3. 主体的に学習に取り組む態度
   - 運動の楽しさや喜びを味わうことができるよう、運動の合理的な実践に主体的に取り組もうとしている。
   - 自他の健康を大切にし、健康の保持増進や回復に関する学習活動に主体的に取り組もうとしている。
# 制約条件
- コメントはこの文章で完結する内容とし、生徒からの返答を求めないようにしてください。
- コメントは前向きで励みになる内容にしてください。
- コメントは200~300文字とする。
`;

    if (this.hasPolarDeviceid) {
      prompt =
        prompt +
        `- 心拍数データがあれば分析し、返答の中で言及してください。
  `;
    }

    let result = "";
    result =
      "#生徒の学習記録" + "\n" + `【単元名】: ${this.unit.data.name}` + "\n\n";
    result =
      result +
      `【${this.graphTitle}】: ` +
      "\n" +
      `${this.reflections
        .map(
          reflection =>
            dayjs.unix(getUnixFromUid(reflection.data.uid)).format("MM月DD日") +
            " : " +
            String(reflection.data.record)
        )
        .join("\n")}` +
      "\n\n";
    result =
      result +
      `【${this.secondRowTitle}】` +
      "\n" +
      `${this.reflections
        .map(
          reflection =>
            dayjs.unix(getUnixFromUid(reflection.data.uid)).format("MM月DD日") +
            " : " +
            reflection.data.reflection
        )
        .join("\n")}` +
      "\n\n";
    result =
      result +
      `【${this.thirdRowTitle}】` +
      "\n" +
      `${this.reflections
        .map(
          reflection =>
            dayjs.unix(getUnixFromUid(reflection.data.uid)).format("MM月DD日") +
            " : " +
            reflection.data.description
        )
        .join("\n")}` +
      "\n\n";

    if (this.hasPolarDeviceid) {
      const heartRates = await this.getHeartRatesFromReflections(
        this.reflections
      );
      result =
        result +
        "\n" +
        "【心拍数の記録】※振り返り日と実際の記録の日付が一致しないことがあります。" +
        this.parseHeartRateData(heartRates, { taleEvery: 5 });

      // MAX_LENGTH以上になったら強制的にお尻から切り取り
      if (result.length > MAX_LENGTH) {
        trimmed = true;
        result = result.slice(0, MAX_LENGTH);
      }
    }

    if (store.state.lang === "en") {
      result =
        result +
        "\n\n" +
        `
      # 必須条件
      返答は必ず英語で返してください。
      `;
    }
    result = result.replace(/^[ \t]+/gm, "");

    const model = "azure-gpt-5-mini";

    if (!isProd) {
      console.warn("送信されるプロンプト ※商用では表示されません");
      console.log(prompt);
      console.log(result);
      console.log(`model: ${model}`);
    }

    const call = firebase
      .app()
      .functions("asia-northeast1")
      .httpsCallable("get_ai_message");
    const res = await call({ prompt, messageText: result, model });
    return { message: res.data.message as string, promptTrimmed: trimmed };
  }

  async updateAiReflection(option: { actionType: "generate" | "delete" }) {
    if (!this.unit) return;
    if (option.actionType !== "generate" && option.actionType !== "delete")
      return;
    const isGenerating = option.actionType === "generate";
    const text = isGenerating ? "作成" : "削除";
    try {
      store.commit("SET_LOADING", true);
      store.commit(
        "SET_LOAD_TEXT",
        `AI振り返り${text}中...(このままお待ちください)`
      );

      const { message = "", promptTrimmed: trimmed = false } = isGenerating
        ? await this.getGptMessage()
        : {};

      await this.unit.ref.update({ aiReflection: message });
      this.$emit("aiReflectionUpdate", {
        unitId: this.unit.data.uid,
        content: message
      });

      if (trimmed) {
        this.promptTrimmed = true;
        setTimeout(() => {
          this.promptTrimmed = false;
        }, 1000 * 5);
      }
    } catch (e) {
      alert(
        `AI振り返りの${text}に失敗しました。` +
          "\n" +
          (e.message ?? "不明なエラー")
      );
      console.error(e);
    } finally {
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    }
  }

  generateAiReflection() {
    if (!this.unit) return;
    if (!this.unit.data.selfReflection) {
      alert(
        "AIの振り返りを利用するには、単元の自身の振り返りを記入してください。"
      );
      return;
    }
    if (this.unit.data.aiReflection) {
      const res = window.confirm(
        "現在登録されている振り返りが上書きされますが、よろしいですか？"
      );
      if (!res) return;
    }

    this.updateAiReflection({ actionType: "generate" });
  }

  deleteAiReflection() {
    const res = window.confirm("AI振り返りを削除してもよろしいですか？");
    if (!res) return;
    this.updateAiReflection({ actionType: "delete" });
  }

  async created(): Promise<void> {
    if (!this.student) {
      alert("生徒情報が取得できません。");
      this.$emit("close");
    }
    if (!this.unit) {
      alert("単元情報が取得できません。");
      this.$emit("close");
    }
    try {
      store.commit("SET_LOADING", true);
      store.commit("SET_LOAD_TEXT", "情報取得中...");
      const _unit = { ...this.unit } as Unit;
      this.unitName = _unit.data.name;
      this.graphTitle = _unit.data.graphTitle || "数値(記録)";
      this.secondRowTitle = _unit.data.secondRowTitle || "授業ごとの振り返り";
      this.thirdRowTitle = _unit.data.thirdRowTitle || "自由入力";

      const _reflections = await this.getReflections();
      this.reflections = sortReflectionsByUid(_reflections).map(reflection =>
        fillMissingTimeRange(reflection)
      );

      if (this.reflections.length > 0) {
        const records = this.reflections.map(_ => +_.data.record);
        if (this.reflections.length > 1) {
          this.min = Math.min(...records) - 1;
          this.max = Math.max(...records) + 1;
        } else {
          const record = +this.reflections[0].data.record;
          this.min = record < 0 ? record - 1 : 0;
          this.max = record + 5;
        }
      }

      this.labels =
        this.reflections.length > 0
          ? this.reflections.map(_ =>
              dayjs.unix(getUnixFromUid(_.data.uid)).format("MM-DD")
            )
          : [];
      this.datasets =
        this.reflections.length > 0
          ? [
              {
                label: this.unitName,
                data: this.reflections.map(_ => _.data.record),
                borderColor: "rgb(75, 192, 192)",
                borderWidth: 2,
                tension: 0.1
              }
            ]
          : [];

      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
    } catch (e) {
      console.error(e);
      alert("情報の取得に失敗しました。");
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      this.$emit("close");
    }
  }
}
